<template>
  <el-container class="panel-container">
    <el-tabs v-model="activeTab" style="height: 100%; width: 100%; overflow: hidden">
      <el-tab-pane :label="i18nt('designer.hint.widgetSetting')" name="1">
        <el-scrollbar class="setting-scrollbar">
          <template v-if="!!designer.selectedWidget && !designer.selectedWidget.category">
            <el-form :model="optionModel" size="small" label-position="left" label-width="120px" class="setting-form" @submit.prevent>
              <el-collapse v-model="widgetActiveCollapseNames" class="setting-collapse">
                <el-collapse-item name="1" v-if="showCollapse(commonProps)" :title="i18nt('designer.setting.commonSetting')">
                  <template v-for="(editorName, propName) in commonProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                    ></component>
                  </template>
                </el-collapse-item>

                <el-collapse-item name="2" v-if="showCollapse(advProps)" :title="i18nt('designer.setting.advancedSetting')">
                  <template v-for="(editorName, propName) in advProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                    ></component>
                  </template>
                </el-collapse-item>

                <el-collapse-item name="3" v-if="showEventCollapse() && showCollapse(eventProps)" :title="i18nt('designer.setting.eventSetting')">
                  <template v-for="(editorName, propName) in eventProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                      :event-handled="getEventHandled(propName)"
                    ></component>
                  </template>
                </el-collapse-item>
              </el-collapse>
            </el-form>
          </template>

          <template v-if="!!designer.selectedWidget && !!designer.selectedWidget.category">
            <el-form :model="optionModel" size="small" label-position="left" label-width="120px" class="setting-form" @submit.prevent>
              <el-collapse v-model="widgetActiveCollapseNames" class="setting-collapse">
                <el-collapse-item name="1" v-if="showCollapse(commonProps)" :title="i18nt('designer.setting.commonSetting')">
                  <template v-for="(editorName, propName) in commonProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                    ></component>
                  </template>
                </el-collapse-item>

                <el-collapse-item name="2" v-if="showCollapse(advProps)" :title="i18nt('designer.setting.advancedSetting')">
                  <template v-for="(editorName, propName) in advProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                    ></component>
                  </template>
                </el-collapse-item>

                <el-collapse-item name="3" v-if="showEventCollapse() && showCollapse(eventProps)" :title="i18nt('designer.setting.eventSetting')">
                  <template v-for="(editorName, propName) in eventProps">
                    <component
                      v-if="hasPropEditor(propName, editorName)"
                      :is="getPropEditor(propName, editorName)"
                      :designer="designer"
                      :selected-widget="selectedWidget"
                      :option-model="optionModel"
                      :event-handled="getEventHandled(propName)"
                    ></component>
                  </template>
                </el-collapse-item>
              </el-collapse>
            </el-form>
          </template>

          <template v-if="!designer.selectedWidget">
            <el-empty :description="i18nt('designer.hint.noSelectedWidgetHint')"></el-empty>
          </template>
        </el-scrollbar>
      </el-tab-pane>

      <el-tab-pane v-if="!!designer" :label="i18nt('designer.hint.formSetting')" name="2">
        <el-scrollbar class="setting-scrollbar">
          <form-setting :designer="designer" :form-config="formConfig"></form-setting>
        </el-scrollbar>
      </el-tab-pane>

      <el-tab-pane :label="i18nt('designer.setting.dataSource')" name="3">
        <el-scrollbar class="ds-setting-scrollbar">
          <data-source-setting :designer="designer" :form-config="formConfig"> </data-source-setting>
        </el-scrollbar>
      </el-tab-pane>
    </el-tabs>

    <div v-if="showWidgetEventDialogFlag" class="" v-drag="['.drag-dialog.el-dialog', '.drag-dialog .el-dialog__header']">
      <el-dialog
        :title="i18nt('designer.setting.editWidgetEventHandler')"
        v-model="showWidgetEventDialogFlag"
        :show-close="true"
        class="drag-dialog small-padding-dialog"
        append-to-body
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :destroy-on-close="true"
      >
        <el-alert type="info" :closable="false" :title="eventHeader"></el-alert>
        <code-editor :mode="'javascript'" :readonly="false" v-model="eventHandlerCode" ref="ecEditor"></code-editor>
        <el-alert type="info" :closable="false" title="}"></el-alert>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="showWidgetEventDialogFlag = false"> {{ i18nt('designer.hint.cancel') }}</el-button>
            <el-button type="primary" @click="saveEventHandler"> {{ i18nt('designer.hint.confirm') }}</el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </el-container>
</template>

<script>
import { computed, getCurrentInstance, reactive, ref, nextTick, toRefs, provide, inject, watch, onMounted } from 'vue';

import CodeEditor from '@/components/code-editor/index';
import PropertyEditors from './property-editor/index';
import FormSetting from './form-setting';
import DataSourceSetting from './data-source-setting';
import WidgetProperties from './propertyRegister';
import { addWindowResizeHandler } from '@/utils/util';
import { useI18n } from '@/utils/i18n';
import { useEmitter } from '@/utils/emitter';
import { propertyRegistered } from '@/components/form-designer/setting-panel/propertyRegister';

const { COMMON_PROPERTIES, ADVANCED_PROPERTIES, EVENT_PROPERTIES } = WidgetProperties;

export default {
  componentName: 'SettingPanel',
  components: {
    CodeEditor,
    DataSourceSetting,
    FormSetting,
    ...PropertyEditors,
  },
  name: 'SettingPanel',
  props: {
    designer: Object,
    formConfig: Object,
    globalDsv: {
      default: () => ({}),
      type: Object,
    },
    selectedWidget: {
      default: () => {
        return {
          options: {},
        };
      },
      type: Object,
    },
  },
  setup(props) {
    const { i18nt } = useI18n();
    const { proxy } = getCurrentInstance();
    const emitter = useEmitter(proxy);
    let $current = proxy;

    const getDesignerConfig = inject('getDesignerConfig');

    const ecEditor = ref(null);
    onMounted(() => {
      if (!props.designer.selectedWidget) {
        data.activeTab = '2';
      } else {
        data.activeTab = '1';
      }
    });

    const data = reactive({
      activeTab: '2',

      advProps: ADVANCED_PROPERTIES,
      commonProps: COMMON_PROPERTIES,
      curEventName: '',

      designerConfig: getDesignerConfig(),
      eventHandlerCode: '',
      eventHeader: '',

      eventProps: EVENT_PROPERTIES,
      formActiveCollapseNames: ['1', '2'],
      showWidgetEventDialogFlag: false,
      subFormChildWidgetFlag: false,

      widgetActiveCollapseNames: ['1', '3'], //['1', '2', '3'],
    });

    provide('isSubFormChildWidget', () => data.subFormChildWidgetFlag);
    provide('getGlobalDsv', data.globalDsv);

    const optionModel = computed({
      get() {
        return props.selectedWidget.options;
      },
      set(newValue) {
        props.selectedWidget.options = newValue;
      },
    });

    /* 设置事件 */
    emitter.on$('editEventHandler', eventParams => {
      $current.editEventHandler(eventParams[0], eventParams[1]);
    });

    $current.designer.handleEvent('form-css-updated', cssClassList => {
      $current.designer.setCssClassList(cssClassList);
    });

    //监听字段组件选中事件
    $current.designer.handleEvent('field-selected', parentWidget => {
      $current.subFormChildWidgetFlag = !!parentWidget && parentWidget.type === 'm-sub-form';
    });

    const getEventHandled = eventName => {
      return !!optionModel.value[eventName] && optionModel.value[eventName].length > 0;
    };

    const showEventCollapse = () => {
      if (data.designerConfig['eventCollapse'] === undefined) {
        return true;
      }

      return !!data.designerConfig['eventCollapse'];
    };

    const hasPropEditor = (propName, editorName) => {
      if (!editorName) {
        return false;
      }

      /* alert组件注册了两个type属性编辑器，跳过第一个type属性编辑器，只显示第二个alert-type属性编辑器！！ */
      if (propName.indexOf('-') <= -1) {
        let uniquePropName = props.selectedWidget.type + '-' + propName;
        if (propertyRegistered(uniquePropName)) {
          return false;
        }
      }

      let originalPropName = propName.replace(props.selectedWidget.type + '-', ''); //去掉组件名称前缀-，如果有的话！！
      return props.designer.hasConfig(props.selectedWidget, originalPropName);
    };
    const getPropEditor = (propName, editorName) => {
      let originalPropName = propName.replace(props.selectedWidget.type + '-', ''); //去掉组件名称前缀-，如果有的话！！
      let ownPropEditorName = `${props.selectedWidget.type}-${originalPropName}-editor`;

      if (!!$current.$options.components[ownPropEditorName]) {
        //局部注册的属性编辑器组件
        return ownPropEditorName;
      }
      return !!$current.$root.$.appContext.components[ownPropEditorName] ? ownPropEditorName : editorName; //Vue3全局注册的属性编辑器组件
    };

    const showCollapse = propsObj => {
      let result = false;

      for (let propName in propsObj) {
        if (!propsObj.hasOwnProperty(propName)) {
          continue;
        }

        if (hasPropEditor(propName, propsObj[propName])) {
          result = true;
          break;
        }
      }

      return result;
    };

    const editEventHandler = (eventName, eventParams) => {
      data.curEventName = eventName;
      data.eventHeader = `${optionModel.value.name}.${eventName}(${eventParams.join(', ')}) {`;
      data.eventHandlerCode = props.selectedWidget.options[eventName] || '';

      // 设置字段校验函数示例代码
      if (eventName === 'onValidate' && !optionModel.value['onValidate']) {
        data.eventHandlerCode =
          "  /* sample codes */\n  /*\n  if ((value > 100) || (value < 0)) {\n    rule.message = 'The value must be between 0 and 100' //set error message\n    return false //fail\n  } else {\n    return true //pass\n  }\n  */";
      }

      data.showWidgetEventDialogFlag = true;
    };

    const saveEventHandler = () => {
      const codeHints = ecEditor.value.getEditorAnnotations();
      let syntaxErrorFlag = false;
      if (!!codeHints && codeHints.length > 0) {
        codeHints.forEach(chItem => {
          if (chItem.type === 'error') {
            syntaxErrorFlag = true;
          }
        });

        if (syntaxErrorFlag) {
          $current.$message.error(i18nt('designer.setting.syntaxCheckWarning'));
          return;
        }
      }

      props.selectedWidget.options[data.curEventName] = data.eventHandlerCode;
      data.showWidgetEventDialogFlag = false;
    };

    watch(
      () => props.selectedWidget,
      (newValue, oldValue) => {
        if (!!newValue) {
          data.activeTab = '1';
        }
      },
      { deep: true }
    );

    watch(
      () => {
        if (props.selectedWidget) return props.selectedWidget.options;
        else return null;
      },
      (newValue, oldValue) => {
        props.designer.saveCurrentHistoryStep();
      },
      { deep: true }
    );

    watch(
      () => props.formConfig,
      (newValue, oldValue) => {
        props.designer.saveCurrentHistoryStep();
      },
      { deep: true }
    );

    return {
      i18nt,
      ...emitter,
      ...toRefs(data),

      /* 组件引用 */
      ecEditor,
      editEventHandler,

      /* 方法 */

      getEventHandled,
      getPropEditor,
      hasPropEditor,

      /* 计算属性 */
      optionModel,
      saveEventHandler,
      showCollapse,
      showEventCollapse,
    };
  },
};
</script>

<style lang="scss" scoped>
.panel-container {
  padding: 0 8px;
  height: 100%;
}

.setting-scrollbar {
  :deep(.el-scrollbar__wrap) {
    overflow-x: hidden; /* IE浏览器隐藏水平滚动条箭头！！ */
  }
}

.ds-setting-scrollbar {
  /*width: 284px;*/
  :deep(.el-scrollbar__wrap) {
    overflow-x: hidden; /* IE浏览器隐藏水平滚动条箭头！！ */
  }
}

.setting-collapse {
  :deep(.el-collapse-item__content) {
    padding-bottom: 6px;
  }

  :deep(.el-collapse-item__header) {
    font-style: italic;
    font-weight: bold;
  }
}

.setting-form {
  :deep(.el-form-item__label) {
    font-size: 13px;
    //text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  :deep(.el-form-item--small.el-form-item) {
    margin-bottom: 10px;
  }
}

/* 隐藏Chrome浏览器中el-input数字输入框右侧的上下调整小箭头 */
:deep(.hide-spin-button) input::-webkit-outer-spin-button,
:deep(.hide-spin-button) input::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
}

/* 隐藏Firefox浏览器中el-input数字输入框右侧的上下调整小箭头 */
:deep(.hide-spin-button) input[type='number'] {
  -moz-appearance: textfield;
}

:deep(.custom-divider.el-divider--horizontal) {
  margin: 10px 0;
}

:deep(.custom-divider-margin-top.el-divider--horizontal) {
  margin: 20px 0;
}

.small-padding-dialog {
  :deep(.el-dialog__body) {
    padding: 6px 15px 12px 15px;
  }
}

:deep(.header-small-mb .el-drawer__header) {
  margin-bottom: 6px;
}

:deep(.header-small-mb .el-drawer__body) {
  padding: 12px;
}

:deep(.el-button.button-text-highlight) {
  font-weight: bold;
  color: $--color-primary;
}
</style>

<style lang="scss">
.ds-setting-drawer {
  right: 320px !important;
}
</style>
